import { Preferences } from "@capacitor/preferences";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { NavigateFunction, redirect } from "react-router-dom";

import { GetResult, Models } from "@/api/types";
import { get, post } from "api";
import { useAppState } from "contexts/AppContext";
import { queryClient } from "pages/Root";
import Services from "services/Services";

export interface AccountSetupParams {
  platformId: string;
  locationId: string;
  navigate: NavigateFunction;
}

// Use the proper generated API models
type CreateUserRequest = Models<"CreateMultiPlatformUserModel">;
type CreateUserResponse = Models<"CreateMultiPlatformUserResult">;
type MultiPlatformListResponse = GetResult<"/multi-platform/list">;

const resolveNextRoute = (user: Models<"UserModel1">): string => {
  if (user.isGuest) {
    return "/home";
  }
  if (!user?.firstName || !user?.lastName) {
    return "/signin/name";
  }
  if (!user?.email) {
    return "/signin/email";
  }
  if (!user?.dateOfBirth) {
    return "/signin/dob";
  }
  if (!user?.signUpSource) {
    return "/signin/source";
  }
  return "/signin/rewards";
};

/**
 * Custom hook for handling the complete account setup process
 * This includes user creation, token management, and navigation to the next onboarding step
 */
export const useAccountSetup = () => {
  const appState = useAppState();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const createUserMutation = useMutation({
    mutationFn: async (locationId: string) => {
      const [
        { value: anonymousToken },
        { value: firstName },
        { value: lastName },
        { value: dobIso },
        { value: email },
        { value: phoneNumber },
      ] = await Promise.all([
        Preferences.get({ key: "multiPlatformVerifyToken" }),
        Preferences.get({ key: "onboarding.firstName" }),
        Preferences.get({ key: "onboarding.lastName" }),
        Preferences.get({ key: "onboarding.dobIso" }),
        Preferences.get({ key: "onboarding.email" }),
        Preferences.get({ key: "phoneNumber" }),
      ]);

      const signupReferralCode =
        localStorage.getItem("referralCode") || undefined;

      const createUserRequest: CreateUserRequest = {
        platformLocationId: locationId,
        phoneNumber: phoneNumber || undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: email || undefined,
        dateOfBirth: dobIso || undefined,
        signUpSource: signupReferralCode ? "friend" : undefined,
        signupReferralCode,
      };

      const response = (await post(
        "/multi-platform/user",
        createUserRequest,
        anonymousToken || undefined
      )) as CreateUserResponse;

      const token = response?.token;
      const platformDomain = response?.platformDomain;
      if (!token || !platformDomain) {
        throw new Error("Failed to create user");
      }

      await Services.auth.setToken(token);

      // Load platform list and cache tokens for future sessions
      const { platformDomainTokens } = (await queryClient.fetchQuery({
        queryKey: ["/multi-platform/list"],
        queryFn: () =>
          get("/multi-platform/list") as Promise<MultiPlatformListResponse>,
      })) as MultiPlatformListResponse;

      const domainScheme = new URL(
        import.meta.env.REACT_APP_API_BASE_URL ?? location.href
      ).protocol;
      const localPlatformId = localStorage.getItem("platformId");
      const fromQrScan = localStorage.getItem("platformLocationId"); // This only exists for QR scans

      if (localPlatformId) {
        if (fromQrScan) {
          // QR scan flow - skip multi-platform validation and proceed directly
          // The platform info comes from the QR code, so we don't need to validate against the multi-platform list
          return redirect("/signin/name");
        } else {
          // Regular platform selection flow - keep existing validation
          const platformDomainModel = platformDomainTokens.find(
            (p) => p.platformId === localPlatformId
          );
          if (!platformDomainModel) {
            return redirect("/signin/platform-picker");
          }

          appState.setApiUrl(
            `${domainScheme}//${platformDomainModel.platformDomain}`
          );
          await Services.auth.setToken(platformDomainModel.token);
          return redirect("/signin/name");
        }
      }

      if (platformDomainTokens.length === 1) {
        localStorage.setItem("platformId", platformDomainTokens[0].platformId);
        appState.setApiUrl(
          `${domainScheme}//${platformDomainTokens[0].platformDomain}`
        );
        await Services.auth.setToken(platformDomainTokens[0].token);
        return redirect("/home?fromSignIn=true");
      }

      return redirect("/signin/platform-picker");
    },
  });

  const handleAccountSetup = async ({
    platformId,
    locationId,
    navigate,
  }: AccountSetupParams) => {
    if (isRedirecting) return;

    setIsRedirecting(true);

    try {
      if (!platformId) {
        setIsRedirecting(false);
        return;
      }

      const existingToken = await Services.auth.getToken();
      const fromQrScan = localStorage.getItem("platformLocationId"); // Detect QR scan context

      // Check if the existing token belongs to a guest user
      const currentUser = await get("/user").catch(() => null);
      const isGuestUser = currentUser?.isGuest === true;

      // Create/convert user if no token exists OR if user is a guest (needs conversion to authenticated)
      if (!existingToken || isGuestUser) {
        await createUserMutation.mutateAsync(locationId);

        // For QR scans, the mutation handles navigation via redirect - return early to avoid double navigation
        if (fromQrScan) {
          setIsRedirecting(false);
          return;
        }
      } else {
        await Preferences.set({
          key: "onboarding.platformLocationId",
          value: locationId,
        });
      }

      // Incrementally update user with only the field on this page in background
      // and navigate immediately to avoid UI flashing back to the list
      await (async () => {
        try {
          const authHeaderToken = await Services.auth.loadToken();
          if (authHeaderToken) {
            await post(
              "/user/update",
              { platformLocationId: locationId },
              authHeaderToken
            );
            await queryClient.invalidateQueries({ queryKey: ["/user"] });
          }
        } catch (err) {
          console.error("[useAccountSetup] update failed", err);
        }
      })();
      // Mark location as confirmed for this session
      localStorage.setItem("locationConfirmed", "true");

      // Decide the next step based on onboarding completeness
      try {
        const user = await get("/user");
        const next = resolveNextRoute(user);
        navigate(next, { replace: true });
      } catch {
        navigate("/home", { replace: true });
      }
    } catch (error) {
      console.error("[useAccountSetup] Account setup failed:", error);
      setIsRedirecting(false);
    }
  };

  return {
    handleAccountSetup,
    isLoading: createUserMutation.isLoading || isRedirecting,
    error: createUserMutation.error,
    isRedirecting,
  };
};
